// ==================== Enums ====================

/**
 * Mirrors the BE `InvestmentStage` enum, including `PaymentMethodSelected` and
 * `FundsReceived` — the intermediate before funds are sent, and the point where
 * the Fund admin has confirmed receipt of the wire/crypto payment but has not
 * yet issued units. See docs/ubiquitous-language.md → "Investment Stage".
 */
export const InvestmentStage = {
  LinkClicked: 'LinkClicked',
  DueDiligenceViewed: 'DueDiligenceViewed',
  SubscriptionStarted: 'SubscriptionStarted',
  EligibilitySubmitted: 'EligibilitySubmitted',
  AgreementSigned: 'AgreementSigned',
  DataSubmissionAcknowledged: 'DataSubmissionAcknowledged',
  PaymentMethodSelected: 'PaymentMethodSelected',
  FundsSent: 'FundsSent',
  FundsReceived: 'FundsReceived',
  Invested: 'Invested',
  Redeemed: 'Redeemed',
  Rejected: 'Rejected',
  NotEligible: 'NotEligible',
  Expired: 'Expired',
} as const;

export type InvestmentStageType = (typeof InvestmentStage)[keyof typeof InvestmentStage];

export type InvestmentCurrency = 'USD' | 'EUR';

export type InvestmentPaymentMethod = 'Crypto' | 'Wire';

/**
 * Investor-facing status, derived from stage + sub-fields — never stored.
 * See docs/ubiquitous-language.md → "Investor-facing status".
 */
export type DerivedInvestorStatus =
  | 'investor eligibility'
  | 'sign agreement'
  | 'data acknowledgment'
  | 'awaiting payment'
  | 'wire in transit'
  | 'funds sent'
  | 'pending units'
  | 'completed'
  | 'rejected'
  | 'not eligible'
  | 'expired'
  | 'redeemed';

// ==================== Domain entities ====================

export interface StageTimelineEntry {
  stage: InvestmentStageType;
  at: string;
}

export interface WireConfirmation {
  date: string;
  bank: string;
  amount: number;
  reference: string;
}

export interface FundsReceivedInfo {
  amount: number;
  date: string;
}

/**
 * The two sequential sub-steps of the Investor Eligibility wizard step.
 * Which ones apply to a given investment is *derived*, not stored — see
 * `getApplicableEligibilitySubSteps` in `./eligibility.ts`. 'subscription'
 * used to be a third sub-step, still present in persisted
 * `eligibilitySubStepsCompleted` arrays — harmless, it's simply never
 * applicable anymore.
 */
export type EligibilitySubStep = 'twoFactor' | 'subscription' | 'tax';

/**
 * The four required Data Acknowledgment checkboxes (ticket 03, wizard step 2).
 * See docs/ubiquitous-language.md → "Investment Stage" and `acknowledgment.ts` for the pure
 * transition that requires all four before advancing the investment.
 */
export type DataAcknowledgmentKey =
  | 'shareWithFund'
  | 'accuracyConfirmation'
  | 'gdprConsent'
  | 'legitimateSource';

/**
 * A document attached to a real subscription. The subscription API tracks the
 * uploaded file, the name the investor gave it, and the slot it was filed
 * under, but no size — so this is deliberately narrower than
 * `SupportingDocumentMeta`.
 *
 * `category` is a plain string, not the two-value union, because the backend
 * stores whatever any client sent: a category a later build introduces has to
 * survive a round-trip through this one rather than be dropped from the
 * replace-PUT. `toSupportingDocumentMeta` is what narrows it for rendering.
 */
export interface SubscriptionDocumentRef {
  fileId: string;
  name: string;
  category: string;
}

export interface SupportingDocumentMeta {
  id: string;
  category: 'ProofOfAddress' | 'SourceOfIncomeOrWealth';
  name: string;
  /** Bytes. Mock upload — no file content is kept, only this metadata. */
  size: number;
}

export interface Investment {
  id: string;
  fundId: string;
  investorId: string;
  /** Display name for an investor other than the signed-in one. */
  investorName?: string;
  stage: InvestmentStageType;
  amount: number;
  currency: InvestmentCurrency;
  paymentMethod: InvestmentPaymentMethod | null;
  wireConfirmation: WireConfirmation | null;
  /**
   * The reference the investor must quote on the wire, issued by the backend
   * when the payment method is selected. Absent on mock-store investments.
   */
  paymentReference?: string | null;
  fundsReceived: FundsReceivedInfo | null;
  unitsIssued: number | null;
  createdAt: string;
  lastActivityAt: string;
  stageTimeline: StageTimelineEntry[];
  /**
   * Investor Eligibility sub-steps completed *for this investment*. Combined
   * with the fund + profile, this derives which sub-step to resume on — see
   * `getCurrentEligibilitySubStep`. Optional so ticket-01 seed data (created
   * before this field existed) stays valid.
   */
  eligibilitySubStepsCompleted?: EligibilitySubStep[];
  supportingDocuments?: SupportingDocumentMeta[];
  /**
   * Documents attached on the real path, where only the file id and name are
   * known. Absent on mock-store investments, which carry the categorized
   * `supportingDocuments` instead.
   */
  subscriptionDocuments?: SubscriptionDocumentRef[];
  /** Data Acknowledgment items checked for this investment — see `acknowledgment.ts`. */
  acknowledgments?: DataAcknowledgmentKey[];
  /** Real Documenso document created for this investment's Sign Agreement step. */
  agreementDocumentId?: string;
  /** Recipient signing token for re-embedding the same document on resume. */
  agreementSigningToken?: string;
}

export interface MockFund {
  id: string;
  identifier: string;
  name: string;
  currency: InvestmentCurrency;
  minInvestment: number;
  /** Drives the eligibility tax-form gate (W-9 vs W-8) — see eligibility.ts. No longer used for payment display. */
  usDomiciled: boolean;
  closedEnded: boolean;
  unitPrice: number;
  legalEntityId: string;
}

export interface TaxFormOnFile {
  formType: 'W-9' | 'W-8BEN' | 'W-8BEN-E';
  signedAt: string;
  expiresAt: string | null;
}

export interface InvestorProfile {
  id: string;
  twoFactorEnabled: boolean;
  taxFormsByLegalEntity: Record<string, TaxFormOnFile>;
  /** Mock personal info used to pre-fill the Subscription Agreement / W-9. */
  fullName: string;
  email: string;
  address: string;
  phone: string;
}
